// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { workspaceAPI, courseAPI, assignmentAPI, meetingAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  FileText, 
  Video, 
  MessageSquare, 
  FolderOpen, 
  Users,
  ArrowRight,
  Clock,
  CalendarDays,
  Wifi,
  WifiOff,
  Monitor,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  ClipboardCheck,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime, isDateFuture, getDaysUntil } from "@/lib/utils";
import LiveAttendanceDashboard from "@/components/presence/LiveAttendanceDashboard";

// Helper: map course title to technology icon URL (Simple Icons CDN)
const TECH_ICON_MAP = [
  { keywords: ['ansible'], icon: 'https://cdn.simpleicons.org/ansible/EE0000', name: 'Ansible' },
  { keywords: ['apache', 'httpd', 'web server'], icon: 'https://cdn.simpleicons.org/apache/D22128', name: 'Apache' },
  { keywords: ['tomcat'], icon: 'https://cdn.simpleicons.org/apachetomcat/F8DC75', name: 'Tomcat' },
  { keywords: ['docker'], icon: 'https://cdn.simpleicons.org/docker/2496ED', name: 'Docker' },
  { keywords: ['kubernetes', 'k8s'], icon: 'https://cdn.simpleicons.org/kubernetes/326CE5', name: 'Kubernetes' },
  { keywords: ['jenkins'], icon: 'https://cdn.simpleicons.org/jenkins/D24939', name: 'Jenkins' },
  { keywords: ['git', 'gitlab'], icon: 'https://cdn.simpleicons.org/gitlab/FC6D26', name: 'GitLab' },
  { keywords: ['linux'], icon: 'https://cdn.simpleicons.org/linux/FCC624', name: 'Linux' },
  { keywords: ['postgres', 'postgresql'], icon: 'https://cdn.simpleicons.org/postgresql/4169E1', name: 'PostgreSQL' },
  { keywords: ['zabbix'], icon: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/zabbix.svg', name: 'Zabbix' },
  { keywords: ['openshift'], icon: 'https://cdn.simpleicons.org/redhatopenshift/EE0000', name: 'OpenShift' },
  { keywords: ['devops', 'cicd', 'ci/cd'], icon: 'https://cdn.simpleicons.org/azuredevops/0078D7', name: 'DevOps' },
  { keywords: ['python'], icon: 'https://cdn.simpleicons.org/python/3776AB', name: 'Python' },
  { keywords: ['nginx'], icon: 'https://cdn.simpleicons.org/nginx/009639', name: 'Nginx' },
  { keywords: ['terraform'], icon: 'https://cdn.simpleicons.org/terraform/844FBA', name: 'Terraform' },
  { keywords: ['prometheus'], icon: 'https://cdn.simpleicons.org/prometheus/E6522C', name: 'Prometheus' },
  { keywords: ['grafana'], icon: 'https://cdn.simpleicons.org/grafana/F46800', name: 'Grafana' },
  { keywords: ['aws', 'amazon'], icon: 'https://cdn.simpleicons.org/amazonaws/FF9900', name: 'AWS' },
  { keywords: ['azure'], icon: 'https://cdn.simpleicons.org/microsoftazure/0078D4', name: 'Azure' },
  { keywords: ['gcp', 'google cloud'], icon: 'https://cdn.simpleicons.org/googlecloud/4285F4', name: 'GCP' },
  { keywords: ['mysql', 'mariadb'], icon: 'https://cdn.simpleicons.org/mysql/4479A1', name: 'MySQL' },
  { keywords: ['mongodb', 'mongo'], icon: 'https://cdn.simpleicons.org/mongodb/47A248', name: 'MongoDB' },
  { keywords: ['redis'], icon: 'https://cdn.simpleicons.org/redis/DC382D', name: 'Redis' },
  { keywords: ['vagrant'], icon: 'https://cdn.simpleicons.org/vagrant/1868F2', name: 'Vagrant' },
  { keywords: ['haproxy'], icon: 'https://cdn.simpleicons.org/haproxy/007DC3', name: 'HAProxy' },
  { keywords: ['nagios'], icon: 'https://cdn.simpleicons.org/nagios/008000', name: 'Nagios' },
];

function getTechIcon(title) {
  if (!title) return null;
  const lower = title.toLowerCase();
  for (const entry of TECH_ICON_MAP) {
    if (entry.keywords.some(kw => lower.includes(kw))) {
      return entry.icon;
    }
  }
  return null;
}
import "./WorkspaceDashboard.css";

/* ── Animated circular gauge ── */
function CircularGauge({ value, max, color, icon: Icon }) {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const offset = circ * (1 - pct);
  const gaugeRef = useRef(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="ws-gauge-ring">
      <svg viewBox="0 0 56 56">
        <circle className="gauge-bg" cx="28" cy="28" r={r} />
        <circle
          ref={gaugeRef}
          className="gauge-fill"
          cx="28" cy="28" r={r}
          strokeDasharray={circ}
          strokeDashoffset={animated ? offset : circ}
        />
      </svg>
      <div className="ws-gauge-icon">
        <Icon />
      </div>
    </div>
  );
}

export default function WorkspacePage() {
  const { workspaceId } = useParams();
  const { canManage } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [activeMeetingsPresence, setActiveMeetingsPresence] = useState([]);
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduledExams, setScheduledExams] = useState([]);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      const [wsRes, statsRes, coursesRes, assignmentsRes, meetingsRes] = await Promise.all([
        workspaceAPI.get(workspaceId),
        workspaceAPI.getStats(workspaceId),
        courseAPI.list(workspaceId),
        assignmentAPI.list(workspaceId),
        meetingAPI.list(workspaceId),
      ]);
      
      setWorkspace(wsRes.data);
      setStats(statsRes.data);
      setAllCourses(coursesRes.data);
      setCourses(coursesRes.data.slice(0, 3));
      setAssignments(assignmentsRes.data.filter(a => a.due_date && isDateFuture(a.due_date)));
      setMeetings(meetingsRes.data.filter(m => isDateFuture(m.scheduled_at)).slice(0, 3));
      
      // Load scheduled exams
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const schedRes = await fetch(`${API_URL}/api/exams/schedules?workspace_id=${workspaceId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (schedRes.ok) {
          const allSchedules = await schedRes.json();
          // Only show upcoming exams (not past)
          const upcoming = allSchedules.filter(s => {
            try { return new Date(s.scheduled_at) > new Date(); } catch(e) { return true; }
          });
          setScheduledExams(upcoming.slice(0, 4));
        }
      } catch (e) { console.error('Failed to load schedules:', e); }
    } catch (error) {
      console.error("Failed to load workspace data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }

    // Fetch active meetings with presence (for managers/trainers)
    if (canManage) {
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/workspaces/${workspaceId}/meetings/active-presence`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setActiveMeetingsPresence(data.active_meetings || []);
        }
      } catch (e) {
        console.error("Failed to load active meetings presence:", e);
      }
    }
  };

  const gaugeCards = [
    { label: "Cours",        value: stats?.total_courses || 0,       icon: BookOpen,       theme: "indigo",  href: "courses",      max: 20 },
    { label: "Devoirs",      value: stats?.total_assignments || 0,   icon: FileText,       theme: "emerald", href: "assignments",  max: 15 },
    { label: "Réunions",     value: stats?.total_meetings || 0,      icon: Video,          theme: "amber",   href: "meetings",     max: 10 },
    { label: "Fichiers",     value: stats?.total_files || 0,         icon: FolderOpen,     theme: "cyan",    href: "files",        max: 50 },
    { label: "Participants", value: stats?.total_participants || 0,  icon: Users,          theme: "rose",    href: "participants", max: 30 },
    { label: "Messages",     value: stats?.total_messages || 0,      icon: MessageSquare,  theme: "violet",  href: "chat",         max: 100 },
  ];

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="h-32 skeleton rounded-2xl mb-6" />
          <div className="ws-gauge-grid">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-24 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* ── Hero Header ── */}
        <div className="ws-hero ws-animate-in">
          <h1>{workspace?.name}</h1>
          {workspace?.description && <p>{workspace.description}</p>}
          {stats && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, position: 'relative', zIndex: 1 }}>
              <TrendingUp style={{ width: 16, height: 16, color: 'rgba(255,255,255,0.7)' }} />
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                {(stats.total_courses || 0) + (stats.total_assignments || 0)} ressources · {stats.total_participants || 0} participants
              </span>
            </div>
          )}
        </div>

        {/* ── Gauge Stats Grid ── */}
        <div className="ws-gauge-grid">
          {gaugeCards.map((g) => (
            <Link
              key={g.label}
              to={`/workspace/${workspaceId}/${g.href}`}
              className="ws-gauge-card ws-animate-in"
              data-theme={g.theme}
              data-testid={`stat-card-${g.href}`}
            >
              <CircularGauge value={g.value} max={g.max} color={g.theme} icon={g.icon} />
              <div className="ws-gauge-info">
                <div className="ws-gauge-value">{g.value}</div>
                <div className="ws-gauge-label">{g.label}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* ── Presence Dashboard (Trainers/Admins) ── */}
        {canManage && (
          <div style={{ marginBottom: '2rem' }}>
            {activeMeetingsPresence.length > 0 && (
              <Card style={{ borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <CardHeader style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', padding: '16px 24px' }}>
                  <CardTitle style={{ color: 'white', fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Video style={{ width: 20, height: 20 }} />
                    Réunions en cours
                    <Badge style={{ background: 'rgba(255,255,255,0.25)', color: 'white', marginLeft: 8, fontSize: 13 }}>
                      {activeMeetingsPresence.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent style={{ padding: 0 }}>
                  {activeMeetingsPresence.map((meeting) => {
                    const p = meeting.presence || {};
                    const isExpanded = expandedMeetingId === meeting.id;
                    return (
                      <div key={meeting.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <div
                          onClick={() => setExpandedMeetingId(isExpanded ? null : meeting.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 24px',
                            cursor: 'pointer', transition: 'background 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--muted)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          {isExpanded
                            ? <ChevronDown style={{ width: 18, height: 18, color: 'var(--muted-foreground)', flexShrink: 0 }} />
                            : <ChevronRight style={{ width: 18, height: 18, color: 'var(--muted-foreground)', flexShrink: 0 }} />
                          }
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 700, fontSize: 14 }}>{meeting.title}</span>
                              <Badge variant="secondary" style={{ fontSize: 11 }}>{meeting.meeting_type}</Badge>
                              {meeting.is_always_active && (
                                <Badge style={{ background: '#22c55e', color: 'white', fontSize: 10 }}>Toujours actif</Badge>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                              <Wifi style={{ width: 14, height: 14, color: '#22c55e' }} />
                              <span style={{ fontWeight: 700, color: '#22c55e' }}>{p.active || 0}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                              <Monitor style={{ width: 14, height: 14, color: '#3b82f6' }} />
                              <span style={{ fontWeight: 700, color: '#3b82f6' }}>{p.external || 0}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                              <Clock style={{ width: 14, height: 14, color: '#f59e0b' }} />
                              <span style={{ fontWeight: 700, color: '#f59e0b' }}>{p.idle || 0}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                              <WifiOff style={{ width: 14, height: 14, color: '#ef4444' }} />
                              <span style={{ fontWeight: 700, color: '#ef4444' }}>{p.absent || 0}</span>
                            </div>
                            <Badge variant="outline" style={{ fontSize: 12 }}>{p.total || 0} participant(s)</Badge>
                          </div>
                        </div>
                        {isExpanded && (
                          <div style={{ padding: '0 24px 20px', background: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
                            <LiveAttendanceDashboard sessionId={`meeting:${meeting.id}`} workspaceId={workspaceId} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {activeMeetingsPresence.length === 0 && (
              <LiveAttendanceDashboard sessionId={workspaceId} workspaceId={workspaceId} />
            )}
          </div>
        )}

        {/* ── Content Sections ── */}
        <div className="ws-content-grid">
          {/* Recent Courses */}
          <div className="ws-section-card ws-animate-in">
            <div className="ws-section-header">
              <div>
                <div className="ws-section-title">
                  <div className="ws-section-icon" style={{ background: 'rgba(99,102,241,0.1)' }}>
                    <BookOpen style={{ width: 18, height: 18, color: '#6366f1' }} />
                  </div>
                  <div>
                    <h3>Cours récents</h3>
                    <div className="ws-section-subtitle">Les derniers cours ajoutés</div>
                  </div>
                </div>
              </div>
              <Link to={`/workspace/${workspaceId}/courses`} className="ws-see-all">
                Voir tout <ArrowRight style={{ width: 14, height: 14 }} />
              </Link>
            </div>
            <div className="ws-section-body">
              {courses.length === 0 ? (
                <div className="ws-empty">Aucun cours pour le moment</div>
              ) : (
                courses.map((course) => (
                  <Link
                    key={course.id}
                    to={`/workspace/${workspaceId}/courses/${course.id}`}
                    className="ws-list-item"
                  >
                    <div className="ws-list-item-icon" style={{ background: 'rgba(99,102,241,0.08)' }}>
                      <BookOpen style={{ width: 18, height: 18, color: '#6366f1' }} />
                    </div>
                    <div className="ws-list-item-content">
                      <div className="ws-list-item-title">{course.title}</div>
                      <div className="ws-list-item-sub">{course.chapter || "Sans chapitre"}</div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Upcoming Meetings */}
          <div className="ws-section-card ws-animate-in">
            <div className="ws-section-header">
              <div>
                <div className="ws-section-title">
                  <div className="ws-section-icon" style={{ background: 'rgba(245,158,11,0.1)' }}>
                    <Video style={{ width: 18, height: 18, color: '#f59e0b' }} />
                  </div>
                  <div>
                    <h3>Réunions à venir</h3>
                    <div className="ws-section-subtitle">Vos prochaines sessions</div>
                  </div>
                </div>
              </div>
              <Link to={`/workspace/${workspaceId}/meetings`} className="ws-see-all">
                Voir tout <ArrowRight style={{ width: 14, height: 14 }} />
              </Link>
            </div>
            <div className="ws-section-body">
              {meetings.length === 0 ? (
                <div className="ws-empty">Aucune réunion prévue</div>
              ) : (
                meetings.map((meeting) => (
                  <Link
                    key={meeting.id}
                    to={`/workspace/${workspaceId}/meetings/${meeting.id}`}
                    className="ws-list-item"
                  >
                    <div className="ws-list-item-icon" style={{ background: 'rgba(245,158,11,0.08)' }}>
                      <Video style={{ width: 18, height: 18, color: '#f59e0b' }} />
                    </div>
                    <div className="ws-list-item-content">
                      <div className="ws-list-item-title">{meeting.title}</div>
                      <div className="ws-list-item-sub" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CalendarDays style={{ width: 12, height: 12 }} />
                        {formatDateTime(meeting.scheduled_at)}
                      </div>
                    </div>
                    <Badge variant="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                      Dans {getDaysUntil(meeting.scheduled_at)}j
                    </Badge>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Scheduled Exams – full width */}
          {scheduledExams.length > 0 && (
            <div className="ws-section-card ws-full-width ws-animate-in">
              <div className="ws-section-header">
                <div>
                  <div className="ws-section-title">
                    <div className="ws-section-icon" style={{ background: 'rgba(16,185,129,0.1)' }}>
                      <ClipboardCheck style={{ width: 18, height: 18, color: '#10b981' }} />
                    </div>
                    <div>
                      <h3>Examens planifiés</h3>
                      <div className="ws-section-subtitle">Prochains examens à passer</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ws-section-body">
                <div className="ws-assignment-grid">
                  {scheduledExams.map((sched) => {
                    const schedDate = new Date(sched.scheduled_at);
                    const now = new Date();
                    const hoursLeft = Math.max(0, Math.round((schedDate - now) / 3600000));
                    const daysLeft = Math.max(0, Math.ceil((schedDate - now) / 86400000));
                    const isToday = schedDate.toDateString() === now.toDateString();
                    return (
                      <div
                        key={sched.id}
                        className={`ws-assignment-item ws-exam-scheduled${isToday ? ' ws-exam-scheduled-today' : ''}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8 }}>
                          <h4 style={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sched.exam_title}
                          </h4>
                          <Badge className="bg-emerald-500 text-white shrink-0" style={{ fontSize: 11 }}>
                            {isToday ? "Aujourd'hui" : `${daysLeft}j`}
                          </Badge>
                        </div>
                        {sched.course_name && (() => {
                          const linkedCourse = allCourses.find(c => c.title === sched.course_name || c.id === sched.course_id);
                          const courseColor = linkedCourse?.color;
                          const techIcon = getTechIcon(sched.course_name);
                          return (
                            <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px 3px 6px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 500, background: courseColor ? `${courseColor}18` : 'hsl(var(--muted) / 0.5)', border: `1px solid ${courseColor || 'hsl(var(--border))'}40`, color: courseColor || 'hsl(var(--muted-foreground))' }}>
                              {techIcon ? (
                                <img src={techIcon} alt="" style={{ width: 13, height: 13 }} />
                              ) : (
                                <BookOpen style={{ width: 11, height: 11, color: courseColor || 'hsl(var(--muted-foreground))' }} />
                              )}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{sched.course_name}</span>
                            </div>
                          );
                        })()}
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          <CalendarDays style={{ width: 12, height: 12 }} />
                          <span>
                            {(() => { try { const d = new Date(sched.scheduled_at); return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch(e) { return sched.scheduled_at; } })()}
                          </span>
                        </div>
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          <Clock style={{ width: 12, height: 12 }} />
                          <span>Durée: {sched.duration_minutes} min</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Assignments – full width */}
          <div className="ws-section-card ws-full-width ws-animate-in">
            <div className="ws-section-header">
              <div>
                <div className="ws-section-title">
                  <div className="ws-section-icon" style={{ background: 'rgba(16,185,129,0.1)' }}>
                    <FileText style={{ width: 18, height: 18, color: '#10b981' }} />
                  </div>
                  <div>
                    <h3>Devoirs</h3>
                    <div className="ws-section-subtitle">Devoirs actifs et à venir</div>
                  </div>
                </div>
              </div>
              <Link to={`/workspace/${workspaceId}/assignments`} className="ws-see-all">
                Voir tout <ArrowRight style={{ width: 14, height: 14 }} />
              </Link>
            </div>
            <div className="ws-section-body">
              {assignments.length === 0 ? (
                <div className="ws-empty">Aucun devoir pour le moment</div>
              ) : (
                <div className="ws-assignment-grid">
                  {assignments.map((assignment) => {
                    const daysLeft = getDaysUntil(assignment.due_date);
                    const isPast = daysLeft < 0;
                    const isActive = !isPast;
                    const notSubmitted = isActive && (assignment.has_submitted === false || assignment.has_submitted === undefined);
                    const isUrgent = isActive && daysLeft <= 1;
                    return (
                      <Link
                        key={assignment.id}
                        to={`/workspace/${workspaceId}/assignments/${assignment.id}`}
                        className={`ws-assignment-item${notSubmitted ? ' ws-assignment-not-submitted' : ''}${isUrgent ? ' ws-assignment-urgent' : ''}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8 }}>
                          <h4 style={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {assignment.title}
                          </h4>
                          <Badge variant={isPast ? "destructive" : daysLeft <= 3 ? "secondary" : "outline"} style={{ flexShrink: 0, fontSize: 11 }}>
                            {isPast ? "Terminé" : `${daysLeft}j`}
                          </Badge>
                        </div>
                        {notSubmitted && (
                          <div className="ws-assignment-warning">
                            <span>⚠️ Non remis</span>
                          </div>
                        )}
                        <p style={{ marginTop: 6, fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {assignment.description || "Pas de description"}
                        </p>
                        {(() => {
                          const linkedCourse = assignment.course_id && allCourses.find(c => c.id === assignment.course_id);
                          if (!linkedCourse) return null;
                          const techIcon = getTechIcon(linkedCourse.title);
                          return (
                            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px 3px 6px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 500, background: linkedCourse.color ? `${linkedCourse.color}18` : 'hsl(var(--muted) / 0.5)', border: `1px solid ${linkedCourse.color || 'hsl(var(--border))'}40`, color: linkedCourse.color || 'hsl(var(--muted-foreground))' }}>
                              {techIcon ? (
                                <img src={techIcon} alt="" style={{ width: 13, height: 13 }} />
                              ) : (
                                <BookOpen style={{ width: 11, height: 11, color: linkedCourse.color || 'hsl(var(--muted-foreground))' }} />
                              )}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{linkedCourse.title}</span>
                            </div>
                          );
                        })()}
                        {canManage && (
                          <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px 3px 6px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 500, background: assignment.is_visible === false ? 'rgba(234,179,8,0.12)' : 'rgba(34,197,94,0.10)', border: `1px solid ${assignment.is_visible === false ? 'rgba(234,179,8,0.4)' : 'rgba(34,197,94,0.35)'}`, color: assignment.is_visible === false ? '#b45309' : '#15803d' }}>
                            {assignment.is_visible === false ? (
                              <><span style={{ fontSize: 13, lineHeight: 1 }}>🙈</span><span>Masqué</span></>
                            ) : (
                              <><span style={{ fontSize: 13, lineHeight: 1 }}>👁️</span><span>Visible</span></>
                            )}
                          </div>
                        )}
                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          <Clock style={{ width: 12, height: 12 }} />
                          <span>Échéance: {formatDate(assignment.due_date)}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
